import { useState, useMemo } from 'react';
import { toastValidation } from '@/lib/toast';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { AssignButton } from '@/components/AssignButton';
import { useAccess } from '@/lib/use-access';
import {
  useTicket,
  useContactTickets,
  useTeamMembers,
  type Ticket,
  type TeamMember,
} from '@/lib/queries';
import type { TicketAction } from '@/lib/api';
import { useTicketAction } from '@/lib/use-ticket-action';
import { useTicketReads } from '@/lib/use-unread';
import { ReminderDialog } from '@/components/ReminderDialog';
import { useTicketReminder } from '@/lib/use-reminders';
import { describeWhen } from '@/lib/reminders';
import { priorityColor, statusColor } from '@/lib/format';
import { RelativeTime } from '@/components/RelativeTime';
import { cn } from '@/lib/utils';
import {
  CheckCircle2,
  AlertOctagon,
  Clock,
  GitMerge,
  Archive,
  ArchiveRestore,
  Ban,
  Bell,
  MoreHorizontal,
  RotateCcw,
  Mail,
  AlarmClock,
} from 'lucide-react';
import { CustomerAssistButton } from '@/components/CustomerAssistButton';
import { ShortcutTooltip } from '@/components/ShortcutTooltip';

type DialogKind = 'resolve' | 'escalate' | 'snooze' | 'merge';

/** Toolbar button sizing: a 36px square below md (icon only, comfortable
 *  to hit one-handed), a normal labelled button from md up. */
const TOOLBAR_BTN = 'max-md:w-9 max-md:px-0';

/** Snooze preset → action name + button label. Custom is its own
 *  branch in the dialog (datetime picker) and not in this list. */
type SnoozePreset = {
  key: 'snooze_1h' | 'snooze_4h' | 'snooze_tomorrow' | 'snooze_monday' | 'snooze_2d';
  label: string;
  hint: string;
};
const SNOOZE_PRESETS: SnoozePreset[] = [
  { key: 'snooze_1h',       label: '1 hour',          hint: 'Quick task buffer' },
  { key: 'snooze_4h',       label: '4 hours',         hint: 'Half a workday' },
  { key: 'snooze_tomorrow', label: 'Tomorrow 9am',    hint: 'Sleep on it' },
  { key: 'snooze_2d',       label: '2 days',          hint: 'Waiting on something external' },
  { key: 'snooze_monday',   label: 'Next Monday 9am', hint: 'Fresh week, fresh look' },
];

interface Props {
  ticketId: string;
  /** Called right after an action was started. The ticket page uses it to open the
   *  next ticket after Resolve / Snooze (see lib/ticket-nav.ts `advancesList`). */
  onActionComplete?: (action: TicketAction) => void;
}

export function TicketActions({ ticketId, onActionComplete }: Props) {
  const { data: ticket } = useTicket(ticketId);

  // What this person may do in THIS ticket's inbox (migration 078). The
  // dispatcher and n8n refuse regardless; this only stops dead buttons.
  const access = useAccess();
  const inboxId = ticket?.inbox_id ?? null;
  const canStatus = access.canIn(inboxId, 'tickets.status');
  const canMerge = access.canIn(inboxId, 'tickets.merge');
  const canArchive = access.canIn(inboxId, 'tickets.archive');
  const canAssist = access.canIn(inboxId, 'assist.start');
  const canAssign =
    access.canIn(inboxId, 'tickets.claim') || access.canIn(inboxId, 'tickets.assign_others');
  const hasMenu = canStatus || canMerge || canArchive;
  const viewOnly = !!ticket && !access.isLoading && !canStatus && !canAssign && !hasMenu && !canAssist;

  const [openDialog, setOpenDialog] = useState<DialogKind | null>(null);
  const [remindOpen, setRemindOpen] = useState(false);
  const reminder = useTicketReminder(ticketId);
  const [reason, setReason] = useState('');

  // Team-member map for the AssignButton (avatars + names on rows).
  const { data: teamMembers } = useTeamMembers();
  const teamMembersBySlackId = useMemo(() => {
    const m = new Map<string, TeamMember>();
    for (const tm of teamMembers ?? []) {
      if (tm.slack_user_id) m.set(tm.slack_user_id, tm);
    }
    return m;
  }, [teamMembers]);
  // Snooze dialog state. `preset` is the selected preset key, or 'custom'
  // when the user wants to pick an exact datetime. `customUntil` is the
  // datetime-local value (browser local TZ) — converted to ISO before
  // sending to the dispatcher.
  const [snoozePreset, setSnoozePreset] = useState<SnoozePreset['key'] | 'custom'>('snooze_1h');
  const [snoozeCustom, setSnoozeCustom] = useState<string>('');
  // Multi-select: which other tickets to pull INTO the current one.
  const [mergeSourceIds, setMergeSourceIds] = useState<Set<string>>(new Set());
  const [mergeFilter, setMergeFilter] = useState<'sameSender' | 'all'>('sameSender');

  // Other tickets from the same sender — primary candidates for merge.
  const senderTicketsQ = useContactTickets(ticket?.sender_email ?? undefined);

  const mergeCandidates = useMemo<Ticket[]>(() => {
    const all = senderTicketsQ.data ?? [];
    return all.filter((t) => t.id !== ticketId);
  }, [senderTicketsQ.data, ticketId]);

  // Every control runs through useTicketAction: the ticket changes on screen
  // (and the success toast shows) the moment it's clicked, and the dispatcher
  // confirms 1-3 s later in the background. A refusal puts the ticket back and
  // turns the toast into an error with Retry. Nothing below is disabled while
  // a request is out — they queue per ticket.
  const { run, runMany } = useTicketAction();
  const { markUnread } = useTicketReads();

  function runAction(action: TicketAction, payload?: Record<string, unknown>) {
    void run(ticketId, action, payload, { subject: ticket?.subject });
    onActionComplete?.(action);
  }

  const toggleMergeSource = (id: string) => {
    setMergeSourceIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const submit = () => {
    if (!openDialog) return;
    if (openDialog === 'merge') {
      if (mergeSourceIds.size === 0) return toastValidation('Pick at least one ticket to merge in');
      // Pull the other tickets INTO this one: each source is closed + archived
      // (so it leaves the lists at once); its messages arrive when the server
      // finishes.
      const sourceIds = Array.from(mergeSourceIds);
      setOpenDialog(null);
      setMergeSourceIds(new Set());
      void runMany(sourceIds, 'merge', { merge_into_ticket_id: ticketId });
      onActionComplete?.('merge');
      return;
    }
    const payload: Record<string, unknown> = {};
    let action: TicketAction;
    if (openDialog === 'snooze') {
      if (snoozePreset === 'custom') {
        if (!snoozeCustom) return toastValidation('Pick a date/time to snooze until');
        // `datetime-local` value is a "naive" string like "2026-05-15T14:30"
        // in the user's browser local TZ. new Date() interprets that as local
        // and gives us the right UTC instant for the dispatcher.
        const d = new Date(snoozeCustom);
        if (isNaN(d.getTime()) || d.getTime() <= Date.now()) {
          return toastValidation('Snooze-until must be in the future');
        }
        action = 'snooze_custom';
        payload.custom_until = d.toISOString();
      } else {
        action = snoozePreset;
      }
    } else {
      action = openDialog; // 'resolve' | 'escalate'
      if (reason.trim()) payload.note = reason.trim();
    }
    // Close first: the dialog has done its job, and the ticket behind it is
    // already showing the result.
    setOpenDialog(null);
    setReason('');
    runAction(action, payload);
  };

  const isClosed = ticket?.status === 'resolved' || ticket?.status === 'closed';
  const isSnoozed = !!ticket?.snoozed_until && new Date(ticket.snoozed_until) > new Date();

  return (
    <>
      {/* Compact action row — lives in the pinned ticket toolbar
          (TicketStickyBar), so it has to stay one line at every width.
            - md+      : Assign, Resolve, Snooze, Escalate, Live assist, ⋯
            - sm..md   : same, labels collapse to icons
            - < sm     : Escalate moves into the ⋯ menu as well
          Dismiss / Merge / Archive always live in ⋯ — they're rarer, and
          Dismiss in particular shouldn't sit one slip away from Resolve.
          Every control still goes through runAction()/setOpenDialog(), so
          the dispatcher stays the single source of action logic
          (CLAUDE.md §5). */}
      <div className="flex shrink-0 items-center gap-1">
        {/* One dropdown for "who owns this ticket": self pinned at top
            (one-click claim), every active teammate below (one-click
            reassign). The dispatcher branches inside the picker —
            'claim' for self, 'reassign' for others. */}
        <ShortcutTooltip label="Assign to me" keys={['a']} side="bottom">
          <AssignButton
            ticketId={ticketId}
            currentAssigneeSlackId={ticket?.assignee_slack_id ?? null}
            teamMembersBySlackId={teamMembersBySlackId}
            compact
          />
        </ShortcutTooltip>

        {!isClosed && canStatus ? (
          <ShortcutTooltip label="Mark resolved" keys={['e']} side="bottom">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setOpenDialog('resolve')}
              aria-label="Resolve"
              className={TOOLBAR_BTN}
            >
              <CheckCircle2 className="h-4 w-4" />
              <span className="hidden md:inline">Resolve</span>
            </Button>
          </ShortcutTooltip>
        ) : null}

        {/* A resolved or dismissed ticket can be brought back with one click. */}
        {isClosed && canStatus ? (
          <Button
            size="sm"
            variant="outline"
            onClick={() => runAction('reopen')}
            aria-label="Reopen"
            title="Reopen this ticket"
            className={TOOLBAR_BTN}
          >
            <RotateCcw className="h-4 w-4" />
            <span className="hidden md:inline">Reopen</span>
          </Button>
        ) : null}

        {/* Snooze toggles to "Wake up" when the ticket is currently
            snoozed. The indigo tint keeps it visually distinct so nobody
            re-snoozes something they meant to revive. */}
        {!canStatus ? null : isSnoozed ? (
          <Button
            size="sm"
            variant="outline"
            onClick={() => runAction('unsnooze')}
            aria-label="Wake up"
            className={cn(
              TOOLBAR_BTN,
              'border-tint-active-ring bg-tint-active text-tint-active-fg hover:bg-tint-active',
            )}
            title={`Currently snoozed until ${new Date(ticket!.snoozed_until!).toLocaleString()} — click to wake up now`}
          >
            <Bell className="h-4 w-4" />
            <span className="hidden md:inline">Wake up</span>
          </Button>
        ) : (
          <Button
            size="sm"
            variant="outline"
            onClick={() => setOpenDialog('snooze')}
            aria-label="Snooze"
            title="Snooze"
            className={TOOLBAR_BTN}
          >
            <Clock className="h-4 w-4" />
            <span className="hidden md:inline">Snooze</span>
          </Button>
        )}

        {/* Remind me: a personal follow-up that, unlike Snooze, leaves the ticket where it is. */}
        {!isClosed ? (
          <Button
            size="sm"
            variant="outline"
            onClick={() => setRemindOpen(true)}
            aria-label={reminder ? `Reminder ${describeWhen(reminder.remind_at)}. Edit` : 'Remind me'}
            title={reminder ? `You'll be reminded ${describeWhen(reminder.remind_at)}. Click to change` : 'Remind me to follow up on this ticket'}
            className={cn(
              TOOLBAR_BTN,
              reminder && 'border-tint-active-ring bg-tint-active text-tint-active-fg hover:bg-tint-active',
            )}
          >
            <AlarmClock className="h-4 w-4" />
            <span className="hidden lg:inline">{reminder ? describeWhen(reminder.remind_at) : 'Remind'}</span>
          </Button>
        ) : null}

        {canStatus ? (
        <Button
          size="sm"
          variant="outline"
          onClick={() => setOpenDialog('escalate')}
          aria-label="Escalate"
          title="Escalate"
          className={cn(TOOLBAR_BTN, 'max-sm:hidden')}
        >
          <AlertOctagon className="h-4 w-4" />
          <span className="hidden md:inline">Escalate</span>
        </Button>
        ) : null}

        {/* Customer Assist — live session for time-sensitive moments
            (2FA codes, billing confirmations). Reusable trigger so the
            session state stays in sync with anything else that mounts it. */}
        {canAssist ? (
          <CustomerAssistButton
            ticketId={ticketId}
            ticketSubject={ticket?.subject ?? undefined}
            variant="toolbar"
          />
        ) : null}

        {viewOnly ? (
          <span
            className="rounded-md border border-border/60 px-2 py-1 text-2xs text-muted-foreground"
            title={`Your role${access.roleName ? ` (${access.roleName})` : ''} can view this ticket but not act on it. Ask an owner or admin for access.`}
          >
            View only
          </span>
        ) : null}

        {/* Always there: "Mark as unread" is personal and needs no permission. */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              size="sm"
              variant="outline"
              aria-label="More actions"
              title="More actions"
              className="w-9 px-0"
            >
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52 max-w-[calc(100vw-1rem)]">
            <DropdownMenuItem
              className="gap-2"
              title="Show this ticket in bold again until you open it"
              onSelect={() => void markUnread(ticketId)}
            >
              <Mail className="h-4 w-4" /> Mark as unread
            </DropdownMenuItem>
            {hasMenu ? <DropdownMenuSeparator /> : null}
            {canStatus ? (
            <DropdownMenuItem
              className="gap-2 sm:hidden"
              onSelect={() => setOpenDialog('escalate')}
            >
              <AlertOctagon className="h-4 w-4" /> Escalate
            </DropdownMenuItem>
            ) : null}
            {canStatus ? (
            <DropdownMenuItem
              className="gap-2"
              title="Dismiss as marketing / spam / no-action — archives and hides from view. Use Resolve for legitimate tickets you've finished."
              onSelect={() => runAction('close')}
            >
              <Ban className="h-4 w-4" /> Dismiss
            </DropdownMenuItem>
            ) : null}
            {canMerge ? (
            <DropdownMenuItem className="gap-2" onSelect={() => setOpenDialog('merge')}>
              <GitMerge className="h-4 w-4" /> Merge…
            </DropdownMenuItem>
            ) : null}
            {canArchive && (canStatus || canMerge) ? <DropdownMenuSeparator /> : null}
            {!canArchive ? null : ticket?.archived_at ? (
              <DropdownMenuItem className="gap-2" onSelect={() => runAction('unarchive')}>
                <ArchiveRestore className="h-4 w-4" /> Unarchive
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem className="gap-2" onSelect={() => runAction('archive')}>
                <Archive className="h-4 w-4" /> Archive
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <ReminderDialog ticketId={ticketId} open={remindOpen} onOpenChange={setRemindOpen} />

      <Dialog open={openDialog !== null} onOpenChange={(open) => !open && setOpenDialog(null)}>
        <DialogContent className={openDialog === 'merge' ? 'max-w-2xl' : ''}>
          <DialogHeader>
            <DialogTitle className="capitalize">{openDialog ?? ''}</DialogTitle>
            <DialogDescription>
              {openDialog === 'resolve' && 'Add a brief resolution note (optional).'}
              {openDialog === 'escalate' && 'Why is this being escalated?'}
              {openDialog === 'snooze' && 'Hide this ticket from the dashboard until later. SLA alerts pause until then. It auto-resurfaces when the timer expires (or click Wake up to bring it back early).'}
              {openDialog === 'merge' &&
                'Pull other tickets INTO this one. Their messages move here, and they get closed + archived. This ticket stays open and keeps its Gmail thread.'}
            </DialogDescription>
          </DialogHeader>

          {openDialog === 'snooze' ? (
            <div className="grid gap-3">
              {/* Preset grid — 2 columns at most so labels stay readable.
                  Each tile has a small hint line under the label to give
                  the user a sense of when each one is the right choice. */}
              <div className="grid grid-cols-2 gap-2">
                {SNOOZE_PRESETS.map((p) => {
                  const isActive = snoozePreset === p.key;
                  return (
                    <button
                      key={p.key}
                      type="button"
                      onClick={() => setSnoozePreset(p.key)}
                      className={cn(
                        'flex flex-col items-start rounded-md border px-3 py-2 text-left transition-colors',
                        isActive
                          ? 'border-foreground bg-foreground/5'
                          : 'border-border hover:border-foreground/40 hover:bg-accent',
                      )}
                    >
                      <span className="text-sm font-medium">{p.label}</span>
                      <span className="text-2xs text-muted-foreground">{p.hint}</span>
                    </button>
                  );
                })}
              </div>

              {/* Custom row — collapses to a single "Custom…" button until
                  clicked, then expands a native datetime-local input. We
                  use the native input because it gets us the platform's
                  date picker (iOS wheel, desktop popup, etc.) for free
                  without a third-party calendar component. */}
              <div>
                <button
                  type="button"
                  onClick={() => setSnoozePreset('custom')}
                  className={cn(
                    'inline-flex h-8 cursor-pointer items-center rounded-md border px-3 text-xs font-medium transition-colors',
                    snoozePreset === 'custom'
                      ? 'border-foreground bg-foreground/5'
                      : 'border-border text-muted-foreground hover:border-foreground/40 hover:text-foreground',
                  )}
                >
                  Pick exact time…
                </button>
                {snoozePreset === 'custom' ? (
                  <div className="mt-2">
                    <Label htmlFor="snooze-custom-until" className="text-2xs font-medium uppercase tracking-wide text-muted-foreground">
                      Wake up at
                    </Label>
                    <input
                      id="snooze-custom-until"
                      type="datetime-local"
                      value={snoozeCustom}
                      onChange={(e) => setSnoozeCustom(e.target.value)}
                      min={new Date(Date.now() + 60_000).toISOString().slice(0, 16)}
                      className="mt-1 h-9 w-full rounded-md border border-border bg-background px-2 text-sm"
                    />
                  </div>
                ) : null}
              </div>
            </div>
          ) : openDialog === 'merge' ? (
            <div className="grid gap-3">
              <div className="flex items-center justify-between gap-2">
                <div className="flex gap-1.5">
                  <button
                    type="button"
                    onClick={() => setMergeFilter('sameSender')}
                    className={cn(
                      'h-7 cursor-pointer rounded-full border px-2.5 text-xs font-medium transition-colors',
                      mergeFilter === 'sameSender'
                        ? 'border-transparent bg-foreground text-background'
                        : 'border-border bg-background text-muted-foreground hover:text-foreground hover:bg-accent',
                    )}
                  >
                    Same sender ({mergeCandidates.length})
                  </button>
                  {/* "All" tab placeholder for v2 — full search across the workspace. */}
                  <button
                    type="button"
                    disabled
                    className="h-7 cursor-not-allowed rounded-full border border-border/50 bg-background px-2.5 text-xs font-medium text-muted-foreground"
                    title="Coming soon — search all tickets"
                  >
                    All (soon)
                  </button>
                </div>
                {mergeCandidates.length > 0 ? (
                  <button
                    type="button"
                    onClick={() => {
                      const allSelected = mergeSourceIds.size === mergeCandidates.length;
                      setMergeSourceIds(
                        allSelected ? new Set() : new Set(mergeCandidates.map((t) => t.id)),
                      );
                    }}
                    className="cursor-pointer text-xs text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
                  >
                    {mergeSourceIds.size === mergeCandidates.length ? 'Clear all' : 'Select all'}
                  </button>
                ) : null}
              </div>
              <div className="max-h-[360px] overflow-y-auto rounded-md border border-border/70 divide-y divide-border/50">
                {senderTicketsQ.isLoading ? (
                  <Skeleton className="h-24 w-full" />
                ) : mergeCandidates.length === 0 ? (
                  <p className="p-4 text-center text-sm text-muted-foreground">
                    No other tickets from {ticket?.sender_email ?? 'this sender'}.
                  </p>
                ) : (
                  mergeCandidates.map((t) => {
                    const selected = mergeSourceIds.has(t.id);
                    return (
                      <label
                        key={t.id}
                        className={cn(
                          'flex w-full cursor-pointer items-center gap-3 px-3 py-2 text-left transition-colors',
                          selected ? 'bg-accent' : 'hover:bg-accent/50',
                        )}
                      >
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={() => toggleMergeSource(t.id)}
                          className="h-4 w-4 cursor-pointer accent-foreground"
                        />
                        <Badge variant="pill" className={cn('w-9 justify-center', priorityColor(t.priority))}>
                          {t.priority ?? '—'}
                        </Badge>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium">
                            {t.subject ?? '(no subject)'}
                          </p>
                          <p className="truncate text-xs text-muted-foreground">
                            opened <RelativeTime value={t.created_at} /> · <code className="text-3xs">{t.id.slice(0, 8)}</code>
                          </p>
                        </div>
                        <Badge variant="pill" className={cn('shrink-0', statusColor(t.status))}>
                          {(t.status ?? '—').replace(/_/g, ' ')}
                        </Badge>
                      </label>
                    );
                  })
                )}
              </div>
              {mergeSourceIds.size > 0 ? (
                <p className="text-xs text-muted-foreground">
                  Merging <strong>{mergeSourceIds.size}</strong> ticket
                  {mergeSourceIds.size === 1 ? '' : 's'} into this one. They'll be{' '}
                  <strong>closed</strong> and their Gmail threads archived.
                </p>
              ) : null}
            </div>
          ) : (
            <div className="grid gap-2">
              <Label htmlFor="reason">Note</Label>
              <Textarea
                id="reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Optional note…"
                rows={3}
              />
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenDialog(null)}>
              Cancel
            </Button>
            <Button
              disabled={openDialog === 'merge' && mergeSourceIds.size === 0}
              onClick={submit}
            >
              {openDialog === 'merge'
                ? `Merge ${mergeSourceIds.size || ''} into this`.trim()
                : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </>
  );
}
